import { trackAiVoiceAuditionReplayed } from '@src/analytics/EventTracker';
import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import MicrophoneIcon from '@src/svgs/MicrophoneIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import { formatRelativeTime } from '@src/utility/relativeTime';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import styled from 'styled-components';

import AudioSamplePlayer from './AudioSamplePlayer';
import VoiceAvatar from './VoiceAvatar';
import { providerLabel } from './voiceLabels';

import type { AuditionHistoryEntry } from '@src/repositories/DubbingRepository';

// The workspace's past auditions (DE12 / PLAT-10580), newest first — each row:
// voice · phrase · language · when, with a replay control that plays the stored
// audio via its fresh signed URL (no re-synthesis). A row whose object has aged
// out (null audioUrl) shows "no longer available" instead of a broken player.
// Presentational: history state + fetching live in `useAuditionHistory`.
interface AuditionHistoryListProps {
  entries: AuditionHistoryEntry[];
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  onShowMore: () => void;
}

const AuditionHistoryList = observer(
  ({
    entries,
    loading,
    error,
    hasMore,
    onShowMore,
  }: AuditionHistoryListProps) => {
    const dubbingStore = useDubbingStore();
    const videoCreator = useVideoCreatorStore();
    // Only one clip plays at a time — the row the user last pressed play on.
    const [activeId, setActiveId] = useState<string | null>(null);

    const languageLabel = (value: string): string => {
      if (!value || value === 'auto') return 'Auto';
      const match = (videoCreator.organization?.supportedLanguages ?? []).find(
        l => l.value === value,
      );
      return match?.caption ?? value;
    };

    const handleReplay = (entry: AuditionHistoryEntry) => {
      setActiveId(entry.auditionId);
      trackAiVoiceAuditionReplayed({
        ai_voice_id: entry.aiVoiceId,
        provider: entry.provider,
        language: entry.language,
      });
    };

    if (loading && entries.length === 0) {
      return <Empty>Loading history…</Empty>;
    }
    if (error && entries.length === 0) {
      return <Empty>Couldn’t load audition history.</Empty>;
    }
    if (entries.length === 0) {
      return (
        <Empty>
          No auditions yet. Audition a voice and it’ll show up here.
        </Empty>
      );
    }

    return (
      <Wrapper>
        <List>
          {entries.map(entry => {
            const voice = dubbingStore.catalog.find(
              v => v.id === entry.aiVoiceId,
            );
            const name =
              voice?.name ?? `${providerLabel(entry.provider)} voice`;
            const isActive = activeId === entry.auditionId;
            return (
              <Row key={entry.auditionId}>
                {voice ? (
                  <VoiceAvatar voice={voice} size={28} />
                ) : (
                  // A voice no longer in the catalog: still the voice grammar
                  // (rounded square + mic), never person initials.
                  <FallbackAvatar aria-hidden>
                    <MicrophoneIcon
                      width="13"
                      height="14"
                      fillColor="currentColor"
                    />
                  </FallbackAvatar>
                )}
                <Body>
                  <TopLine>
                    <Name>{name}</Name>
                    <When>{formatRelativeTime(entry.createdAt)}</When>
                  </TopLine>
                  <Phrase title={entry.text}>{entry.text}</Phrase>
                  <Meta>
                    <span>{languageLabel(entry.language)}</span>
                    {entry.createdByName && (
                      <span className="by">· {entry.createdByName}</span>
                    )}
                  </Meta>
                  {isActive && entry.audioUrl && (
                    <AudioSamplePlayer url={entry.audioUrl} autoPlay />
                  )}
                </Body>
                {entry.audioUrl ? (
                  !isActive && (
                    <Replay
                      type="button"
                      onClick={() => handleReplay(entry)}
                      aria-label={`Replay ${name}`}
                    >
                      <MuxPlayIcon
                        width="12"
                        height="12"
                        fillColor="currentColor"
                      />
                    </Replay>
                  )
                ) : (
                  <Unavailable>No longer available</Unavailable>
                )}
              </Row>
            );
          })}
        </List>
        {hasMore && (
          <ShowMore type="button" onClick={onShowMore} disabled={loading}>
            {loading ? 'Loading…' : 'Show more'}
          </ShowMore>
        )}
      </Wrapper>
    );
  },
);

export default AuditionHistoryList;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// A bordered table of audition rows (dividers, not separate cards). Capped so a
// long history scrolls internally instead of growing the Settings panel off the
// screen; "Show more" stays below in the Wrapper.
const List = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 8px;
  max-height: 340px;
  overflow-y: auto;
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 10px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const FallbackAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 8px;
  background: var(--surface-elevated);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-bold);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const TopLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const Name = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
`;

const When = styled.span`
  flex-shrink: 0;
  color: var(--text-muted);
  font-size: var(--text-xs);
`;

const Phrase = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-secondary);
`;

const Meta = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  color: var(--text-muted);
  font-size: var(--text-xs);

  .by {
    color: var(--text-secondary);
  }
`;

const Replay = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: var(--primary);
  color: var(--background);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: var(--transition-normal);

  &:hover {
    filter: brightness(1.2) saturate(1.2);
  }
`;

const Unavailable = styled.span`
  flex-shrink: 0;
  align-self: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-style: italic;
`;

const Empty = styled.p`
  margin: 0;
  padding: 24px 12px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  text-align: center;
`;

const ShowMore = styled.button`
  align-self: center;
  padding: 6px 14px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  color: var(--foreground);
  font-size: var(--text-sm);
  cursor: pointer;
  transition: var(--transition-normal);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
